import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Container from './Container';
import Button from '../ui/Button';
import CurrencySelector from '../ui/CurrencySelector';
import logo from '../../assets/logo/codemy-ai-logo.png';

// Navigation menu items
const navItems = [
  { label: 'Home', path: '/' },
  {
    label: 'Services',
    path: '/services',
    submenu: [
      { label: 'AI Training', path: '/services/ai-training' },
      { label: 'AI Onboarding', path: '/services/ai-onboarding' },
      { label: 'Workflow Automation', path: '/services/workflow-automation' },
      { label: 'CRM & Pipeline', path: '/services/crm-pipeline-automation' },
      { label: 'Custom AI Dev', path: '/services/custom-ai-development' },
      { label: 'AI Optimisation', path: '/services/ai-optimization' },
      { label: 'Web Design', path: '/services/web-design' },
      { label: 'Software Dev', path: '/services/software-development' },
      { label: 'SEO', path: '/services/seo' },
      { label: 'Digital Marketing', path: '/services/digital-marketing' },
    ],
  },
  {
    label: 'Tools',
    path: '/tools',
    submenu: [
      { label: 'AI Service Selector', path: '/tools/service-selector' },
      { label: 'Pipeline ROI Calculator', path: '/tools/pipeline-roi-calculator' },
    ],
  },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

/**
 * Main site header component with navigation
 */
const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<number | null>(null);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Handle submenu click
  const handleSubmenuClick = (index: number) => {
    if (activeSubmenu === index) {
      setActiveSubmenu(null);
    } else {
      setActiveSubmenu(index);
    }
  };

  return (
    <header
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-lg py-2' : 'bg-white py-4 border-b border-gray-200'
      }`}
    >
      <Container>
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <div
              className="rounded-2xl overflow-hidden transition-all duration-300 group-hover:-translate-y-0.5"
              style={{
                boxShadow:
                  '5px 5px 10px rgb(163,177,198,0.6), -5px -5px 10px rgba(255,255,255,0.5)',
              }}
            >
              <img src={logo} alt="CodeMyAI" className="h-12 md:h-14 w-auto block" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8 bg-white py-2 px-4 rounded-full shadow-sm border border-gray-100">
            <ul className="flex space-x-8">
              {navItems.map((item, idx) => (
                <li key={idx} className="relative group">
                  {item.submenu ? (
                    <div className="flex items-center cursor-pointer">
                      <span
                        className={`text-gray-800 hover:text-primary-600 font-semibold transition-colors duration-200 ${
                          location.pathname.includes(item.path) && item.path !== '/'
                            ? 'text-primary-600'
                            : ''
                        }`}
                      >
                        {item.label}
                      </span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 ml-1 transition-transform duration-200 group-hover:rotate-180"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`text-gray-800 hover:text-primary-600 font-semibold transition-colors duration-200 ${
                        location.pathname === item.path ||
                        (item.path !== '/' && location.pathname.includes(item.path))
                          ? 'text-primary-600'
                          : ''
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}

                  {/* Submenu for desktop */}
                  {item.submenu && (
                    <div className="absolute left-0 mt-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-top-left z-50">
                      <div className="pt-2">
                        <div className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
                          <div className="py-2">
                            {item.submenu.map((subItem, subIdx) => (
                              <Link
                                key={subIdx}
                                to={subItem.path}
                                className="block px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200"
                              >
                                {subItem.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <div className="flex items-center space-x-4">
              <CurrencySelector variant="minimal" />
              <Button
                variant="primary"
                size="sm"
                onClick={() => (window.location.href = '/book')}
                className="shadow-md hover:shadow-lg"
              >
                Book a Free Call
              </Button>
            </div>
          </nav>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-primary-600 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              onClick={toggleMenu}
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </Container>

      {/* Mobile menu */}
      <div className={`lg:hidden ${isMenuOpen ? 'block' : 'hidden'}`}>
        <div className="border-t border-gray-200 bg-white">
          <div className="px-2 pt-2 pb-3 space-y-1 shadow-xl">
            {navItems.map((item, idx) => (
              <div key={idx}>
                {item.submenu ? (
                  <div>
                    <button
                      className={`w-full flex items-center justify-between px-3 py-2 text-base font-semibold rounded-md ${
                        location.pathname.includes(item.path) && item.path !== '/'
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-gray-800 hover:text-primary-600 hover:bg-primary-50'
                      }`}
                      onClick={() => handleSubmenuClick(idx)}
                    >
                      <span>{item.label}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-4 w-4 transition-transform duration-200 ${
                          activeSubmenu === idx ? 'rotate-180' : ''
                        }`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    {activeSubmenu === idx && (
                      <div className="pl-4 py-2 space-y-1">
                        {item.submenu.map((subItem, subIdx) => (
                          <Link
                            key={subIdx}
                            to={subItem.path}
                            className={`block px-3 py-2 text-sm font-medium rounded-md ${
                              location.pathname === subItem.path
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-primary-50'
                            }`}
                            onClick={toggleMenu}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={`block px-3 py-2 text-base font-semibold rounded-md ${
                      location.pathname === item.path ||
                      (item.path !== '/' && location.pathname.includes(item.path))
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-gray-800 hover:text-primary-600 hover:bg-primary-50'
                    }`}
                    onClick={toggleMenu}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
            <div className="pt-4 border-t border-gray-200">
              <div className="px-3 py-2">
                <CurrencySelector variant="minimal" />
              </div>
              <Button
                variant="primary"
                className="mx-3 w-full shadow-md"
                onClick={() => (window.location.href = '/book')}
              >
                Book a Free Call
              </Button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
